import { Calendar, Clock, Mail, MapPin, Package, User } from 'lucide-react';
import { formatPlanStartTime, officeDayKey, type VendorPortalBooking } from '@peles/shared';
import { BookingCard } from '../bookings/BookingCard';
import { describeBookingParts, describeMoney, moneyPaidPercent } from '../bookings/bookingDisplay';
import { PhoneLink } from '../common/PhoneLink';

export interface VendorBookingCardProps {
  booking: VendorPortalBooking;
  eventName: string;
  /** The event's start, an ISO timestamp. */
  eventDate: string;
  venue: { name: string; address: string | null } | null;
  /** Name the vendor on the card — when the person works for more than one here. */
  showVendorName?: boolean;
}

/**
 * "Sat, Oct 18, 2026" — the event's day in the office's time zone, the day it
 * is held, never the day it falls on where the vendor's browser happens to be.
 */
export function eventDay(iso: string): string {
  return new Date(`${officeDayKey(new Date(iso))}T12:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** "Arrive 3:00 PM · Load-in 1:30 PM", or "" with neither. */
export function describeDayOfTimes(booking: VendorPortalBooking): string {
  const arrive = formatPlanStartTime(booking.arrivalTime);
  const loadIn = formatPlanStartTime(booking.loadInTime);
  return [arrive && `Arrive ${arrive}`, loadIn && `Load-in ${loadIn}`]
    .filter((part): part is string => !!part)
    .join(' · ');
}

/**
 * The vendor's own booking on an event — the office's booking card, read-only:
 * the event, its day and venue, the stage, when to arrive and load in, who to
 * call on the day, what is booked, and the money only when the office shares it.
 */
export function VendorBookingCard({
  booking,
  eventName,
  eventDate,
  venue,
  showVendorName = false,
}: VendorBookingCardProps) {
  const hasNeeds = booking.needs.length > 0;
  const moneyText = booking.money ? describeMoney(booking.money, hasNeeds) : '';
  const times = describeDayOfTimes(booking);
  const contact = booking.dayOfContact;
  const row = 'flex items-start gap-2 text-sm text-gray-700';

  return (
    <BookingCard
      className="p-4 space-y-3 bg-white rounded-lg border"
      title={eventName}
      description={[
        showVendorName ? booking.vendorName : null,
        describeBookingParts({
          categories: booking.categories,
          needCount: booking.needs.length,
        }),
      ]
        .filter(Boolean)
        .join(' · ')}
      stage={booking.stage}
      money={
        booking.money && moneyText
          ? {
              text: moneyText,
              paidPercent: moneyPaidPercent(booking.money, hasNeeds),
              label: `${booking.vendorName} paid`,
            }
          : null
      }
    >
      <div className="space-y-2" data-testid={`vendor-booking-${booking.vendorId}`}>
        <p className={row}>
          <Calendar size={16} className="mt-0.5 shrink-0 text-gray-400" aria-hidden />
          <span>{eventDay(eventDate)}</span>
        </p>
        {venue && (
          <p className={row}>
            <MapPin size={16} className="mt-0.5 shrink-0 text-gray-400" aria-hidden />
            <span>
              {venue.name}
              {venue.address && (
                <span className="block text-xs text-gray-500">{venue.address}</span>
              )}
            </span>
          </p>
        )}
        {times && (
          <p className={row}>
            <Clock size={16} className="mt-0.5 shrink-0 text-gray-400" aria-hidden />
            <span>{times}</span>
          </p>
        )}
        {contact && (
          <div className={row}>
            <User size={16} className="mt-0.5 shrink-0 text-gray-400" aria-hidden />
            <div className="min-w-0">
              <p>
                <span className="text-gray-500">Your contact on the day: </span>
                {contact.name}
              </p>
              <div className="flex flex-wrap items-center gap-x-3">
                <PhoneLink phone={contact.phone} name={contact.name} />
                {contact.email && (
                  <a
                    href={`mailto:${contact.email}`}
                    className="inline-flex items-center gap-1 min-h-11 sm:min-h-0 text-primary-600 hover:text-primary-700 break-all"
                  >
                    <Mail size={14} aria-hidden />
                    {contact.email}
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
        {hasNeeds && (
          <div className={row}>
            <Package size={16} className="mt-0.5 shrink-0 text-gray-400" aria-hidden />
            <ul className="min-w-0 space-y-0.5" aria-label="Booked with you">
              {booking.needs.map((need) => (
                <li key={need.id}>{need.name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </BookingCard>
  );
}
